import { useEffect, useMemo } from "react";
import "./style/global.css";
import "./style/scrollbar.css";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import { getTheme } from "./style/theme";
import { PaletteMode } from "@mui/material/styles";
import { RouteObserver } from "@routing/RouteObserver";
import { useProducts } from "@shop/hooks/useProducts";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import { useUserProfile } from "@company/hooks/useUserProfile";
import { useLocation, useNavigate } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";
import { usePostHog } from "posthog-js/react";
import mixpanel from "mixpanel-browser";
import GlobalErrorBoundary from "@core/error/GlobalErrorBoundary";
import AppLayout from "@core/layout/AppLayout";
import { useOnMobile } from "@core/hooks/useOnMobile";
import MobileUnavailableScreen from "./components/MobileUnavailableScreen";
import { setAuthHandler, setDispatch } from "./graphql/client";
import useAuthActions from "@auth/hooks/useAuthActions";
import { useDispatch } from "react-redux";

// PRODUCTION SAFETY: Load development helpers only in development
if (process.env.NODE_ENV === 'development') {
    // Use dynamic import with error handling to prevent any production issues
    import("@/utils/devHelpers").catch((error) => {
        console.warn('⚠️ Could not load development helpers:', error.message);
    });
}

// Initialize Mixpanel
if (process.env.REACT_APP_MIXPANEL_TOKEN) {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV === "development",
        track_pageview: true,
        persistence: "localStorage",
        autocapture: {
            pageview: "full-url",
            click: true,
            input: true,
            scroll: true,
            submit: true,
            capture_text_content: false,
        },
    });
}

function App() {
    const mode: PaletteMode = "light";
    const isAuthenticated = useIsAuthenticated();
    const { user } = useUserProfile();
    const navigate = useNavigate();
    const theme = useMemo(() => createTheme(getTheme(mode)), [mode]);
    const { pathname } = useLocation();
    const posthog = usePostHog();
    const onMobile = useOnMobile();
    const { logout } = useAuthActions();
    const dispatch = useDispatch();

    // Public routes that should be accessible without authentication
    const PUBLIC_PATHS: string[] = [
        RouteTo.LOGIN,
        RouteTo.ONBOARDING,
        RouteTo.PRIVACY,
        RouteTo.AGB,
        RouteTo.CONTACT,
        RouteTo.USER_GUIDE,
        RouteTo.LEGAL,
        RouteTo.FORGOT_PASSWORD,
        RouteTo.RESET_PASSWORD,
        RouteTo.INVITE
    ];

    // Mixpanel user identification
    useEffect(() => {
        if (isAuthenticated && user && process.env.REACT_APP_MIXPANEL_TOKEN) {
            mixpanel.identify(user.id);
            mixpanel.people.set({
                $name: user.name || "Unknown",
                $email: user.email,
                role: user.role,
                title: user.title || "",
                emailVerified: user.emailVerified,
                usedPages: user.usedPages,
                pageQuota: user.pageQuota,
            });
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        // PostHog pageview tracking
        if (posthog) {
            posthog.capture("$pageview");
        }
    }, [pathname, posthog]);

    useEffect(() => {
        if (!isAuthenticated) {
            const isPublic = PUBLIC_PATHS.includes(pathname) || pathname.startsWith(RouteTo.INVITE);
            if (!isPublic) {
                navigate(RouteTo.LOGIN);
            }
        }
    }, [isAuthenticated, navigate, pathname]);

    // Register handlers for Apollo Client authentication errors
    useEffect(() => {
        console.log("🔧 Registering auth handler and dispatch for Apollo Client");
        
        // Register auth handler
        setAuthHandler((skipNotification?: boolean) => {
            console.log("🚨 Auth handler triggered - performing logout", { skipNotification });
            logout(true, skipNotification); // true = navigate, skipNotification = custom handling
        });
        
        // Register Redux dispatch for notifications
        setDispatch(dispatch);
        
        // Cleanup on unmount
        return () => {
            console.log("🔧 Unregistering auth handler and dispatch");
            setAuthHandler(null);
            setDispatch(null);
        };
    }, [logout, dispatch]);

    // Lädt die Preise der Produkte vor
    useProducts();

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {onMobile ? (
                <MobileUnavailableScreen />
            ) : (
                <GlobalErrorBoundary>
                    <RouteObserver />
                    <AppLayout />
                </GlobalErrorBoundary>
            )}
        </ThemeProvider>
    );
}

export default App;
